import { useCallback, useEffect, useMemo } from 'react';
import { useCrossTabCount } from 'hooks/useCrossTabCount';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { EBroadcastChannel, EFeatureFlag } from 'constants/enums';
import { OfferData } from 'constants/apiResponses.types';

import {
  markDismissed,
  isOfferEligible,
  pickEligibleStoreRefreshOffer,
} from './useStoreRefresh.utils';
import { SHOWING_STALE_MILLISECONDS } from './useStoreRefresh.consts';

const TAB_ID = crypto.randomUUID();

interface IShowingLockPayload {
  tabId: string;
  timestampMilliseconds: number;
}

const isAnotherTabShowing = (): boolean => {
  const rawValue = localStorage.getItem(EBroadcastChannel.STORE_REFRESH_SHOWING_KEY);
  if (rawValue === null) return false;
  try {
    const { tabId, timestampMilliseconds } = JSON.parse(rawValue) as IShowingLockPayload;
    if (tabId === TAB_ID) return false;
    if (!Number.isFinite(timestampMilliseconds)) return false;
    return Date.now() - timestampMilliseconds < SHOWING_STALE_MILLISECONDS;
  } catch {
    return false;
  }
};

const writeShowingLock = (): void => {
  const payload: IShowingLockPayload = {
    tabId: TAB_ID,
    timestampMilliseconds: Date.now(),
  };
  localStorage.setItem(
    EBroadcastChannel.STORE_REFRESH_SHOWING_KEY,
    JSON.stringify(payload),
  );
};

const clearShowing = (): void => {
  const rawValue = localStorage.getItem(EBroadcastChannel.STORE_REFRESH_SHOWING_KEY);
  if (rawValue === null) return;
  try {
    const { tabId } = JSON.parse(rawValue) as Pick<IShowingLockPayload, 'tabId'>;
    if (tabId !== TAB_ID) return;
  } catch {
    // Corrupted entry — safe to remove
  }
  localStorage.removeItem(EBroadcastChannel.STORE_REFRESH_SHOWING_KEY);
};

interface IUseStoreRefreshProps {
  offers: OfferData[];
}

interface IUseStoreRefreshReturn {
  count: number;
  eligibleOffer: OfferData | undefined;
  isOpen: boolean;
  isBadgeEligible: boolean;
  onCollect: (offerId: string) => void;
  onClose: (offerId: string) => void;
}

/**
 * Hook that manages store-refresh popup eligibility, cross-tab locking,
 * and dismiss handling for one or more store-refresh offers.
 *
 * @example
 * const {
 *   eligibleOffer,
 *   isOpen,
 *   isBadgeEligible,
 *   onCollect,
 *   onClose,
 * } = useStoreRefresh({ offers: storeRefreshOffers });
 */
export const useStoreRefresh = (
  props: IUseStoreRefreshProps,
): IUseStoreRefreshReturn => {
  const { offers } = props;

  const isEnabled = useFeatureFlag(EFeatureFlag.POPUP_STORE_REFRESH_ENABLED);
  const { count, reset } = useCrossTabCount({
    channelName: EBroadcastChannel.STORE_REFRESH_CHANNEL_NAME,
    storageKey: EBroadcastChannel.STORE_REFRESH_STORAGE_KEY,
    autoIncrement: isEnabled && offers.length > 0,
  });

  useEffect((): (() => void) => {
    return (): void => {
      clearShowing();
    };
  }, []);

  const eligibleOffer = useMemo(
    (): OfferData | undefined =>
      isEnabled ? pickEligibleStoreRefreshOffer(offers, count) : undefined,
    [isEnabled, offers, count],
  );

  const isOpen = useMemo((): boolean => {
    if (!isEnabled || !eligibleOffer) return false;
    return !isAnotherTabShowing();
  }, [isEnabled, eligibleOffer]);

  useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined;
    writeShowingLock();
    const interval = setInterval(writeShowingLock, SHOWING_STALE_MILLISECONDS / 2);
    return (): void => clearInterval(interval);
  }, [isOpen]);

  const isBadgeEligible = useMemo(
    (): boolean => !!isEnabled && offers.some((offer) => isOfferEligible(offer, count)),
    [isEnabled, offers, count],
  );

  const dismiss = useCallback((offerId: string): void => {
    markDismissed(offerId);
    clearShowing();
    reset();
  }, [reset]);

  return {
    count,
    eligibleOffer,
    isOpen,
    isBadgeEligible,
    onCollect: dismiss,
    onClose: dismiss,
  };
};
